import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  DatePicker,
  DeleteButton,
  Form,
  FormField,
  FormRow,
  FormSection,
  Input,
  ModalFooter,
  Select,
  Table,
  TableCell,
  TableHeader,
  TableHeaders,
  TableRow,
  Text,
} from '../ui';
import * as zod from 'zod';
import type {
  Control,
  FieldArrayWithId,
  FieldPath,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayReturn,
  UseFormRegister,
  UseFormReturn,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ReactSelect from 'react-select';
import CreatableSelect from 'react-select/creatable';
import compact from 'lodash/compact';
import startCase from 'lodash/startCase';
import { toast } from 'react-toastify';

import type {
  CoaForm_AssortmentFragment,
  CoaForm_CoaAssortmentFragment,
  CoaForm_CoaBunkerFragment,
  CoaForm_CoaFragment,
  CoaForm_CoaPriceFragment,
  CoaForm_CoaVesselFragment,
  CoaForm_CommissionFragment,
  CoaForm_PortPairFragment,
  CoaFormQuery,
  CoaFormQueryResult,
  Exact,
  Maybe,
  CoaForm_AssortmentGroupFragment,
  CoaForm_PortCostFragment,
} from '../../graphql';
import {
  BafTypes,
  BunkerGrade,
  CommissionType,
  Currency,
  PaymentTrigger,
  PaymentType,
  PaymentUnit,
  useCoaFormQuery,
  useCreateChartererMutation,
  useCreateOrganizationMutation,
  useAllVesselAttributesQuery,
} from '../../graphql';
import {
  currencies,
  paymentTriggers,
  paymentTypes,
  paymentUnits,
} from '../../constants/paymentUnits';
import { isDefined, mapKeyBy, setIntersection } from '../../lib/util';
import type { LocationZoneSelectValue } from '../LocationSelect';
import { LocationZoneSelect } from '../LocationSelect';
import {
  dateToString,
  valueOrNull,
  nullableStrToEnum,
  stringToNumber,
} from '../../lib/zod-util';
import {
  nodesToOptions,
  paymentUnitsToOptions,
  reactSelectStyles,
} from '../../lib/form-util';
import PortDataForm, {
  portDataNodesToPortData,
  portDataSaveSchema,
  portDataSchema,
} from './PortDataForm';
import { generateClientId } from '../core/ClientId';
import { stringToNumOpt } from '../core/util';
import type { ApolloQueryResult } from '@apollo/client';
import { useCurrentUser } from '../../state/zustand';
import { reducedUnits } from '../../constants/cargoUnits';

// Need to add the 'id-' prefix get around react-hook-form's issues with objects that have numbers as keys
const numberKeyPrefix = 'id-';

const CHECK_BOX_SIZE = {
  width: 20,
  height: 20,
};

const baseContractSchema = zod.object({
  shipownerOrgId: zod.object({
    value: zod.number().min(1),
    label: zod.string(),
  }),
  chartererOrgId: zod.object({
    value: zod.number().min(1),
    label: zod.string(),
  }),
  name: zod.string().min(1),
  startDate: zod.date(),
  endDate: zod.date(),
  minAmount: zod.number().nullable(),
  maxAmount: zod.number().nullable(),
  amountUnitId: zod.number().nullable(),
  currency: nullableStrToEnum(Currency),
  paymentDays: zod.number().nullable(),
  paymentTrigger: nullableStrToEnum(PaymentTrigger),
  paymentType: nullableStrToEnum(PaymentType),
  externalId: zod.string().nullable(),
});

const bunkerSchema = zod.object({
  id: zod.number().nullable(),
  bunkerGrade: zod.nativeEnum(BunkerGrade),
  price: zod.number(),
  basePriceMax: zod.number().nullable(),
  bafType: nullableStrToEnum(BafTypes),
  bafStepSize: zod.number().nullable(),
  bafStepEffect: zod.number().nullable(),
  bafStartFromBase: zod.boolean().nullable(),
  bafUseStepEffectAsValue: zod.boolean().nullable(),
  bafBunkerAmount: zod.number().nullable(),
  coaRouteId: zod.union([zod.number(), zod.string()]).nullable(),
  coaAssortmentId: zod.union([zod.number(), zod.string()]).nullable(),
});
const bunkersSchema = zod.array(bunkerSchema);

type BunkerType = zod.infer<typeof bunkerSchema>;

const portPairSchema = zod.object({
  id: zod.union([zod.number(), zod.string()]),
  originId: zod
    .object({
      value: zod.number(),
      label: zod.string(),
    })
    .nullable(),
  destinationId: zod
    .object({
      value: zod.number(),
      label: zod.string(),
    })
    .nullable(),
  originZoneId: zod
    .object({
      value: zod.number(),
      label: zod.string(),
    })
    .nullable(),
  destinationZoneId: zod
    .object({
      value: zod.number(),
      label: zod.string(),
    })
    .nullable(),
});
type PortPairType = zod.infer<typeof portPairSchema>;
const portPairsSchema = zod.array(portPairSchema);

const assortmentSchema = zod.object({
  id: zod.union([zod.number(), zod.string()]),
  assortmentId: zod.number().nullable(),
  assortmentGroupId: zod.number().nullable(),
  paymentUnit: zod.nativeEnum(PaymentUnit),
  minAmount: zod.number().nullable(),
  maxAmount: zod.number().nullable(),
});
type AssortmentType = zod.infer<typeof assortmentSchema>;
const assortmentsSchema = zod.array(assortmentSchema);

const priceSchema = zod.object({
  id: zod.number().nullable(),
  shipId: zod.number().nullable(),
  price: zod.number().nullable(),
});
type PriceType = zod.infer<typeof priceSchema>;

const pricesSchema = zod.record(zod.record(priceSchema));

const vesselSchema = zod.object({
  id: zod.number().nullable(),
  shipId: zod.object({
    value: zod.number(),
    label: zod.string(),
  }),
  demurrage: zod.number().nullable(),
});

const vesselsSchema = zod.array(vesselSchema);

export const commissionSchema = zod.object({
  id: zod.number().nullable(),
  coaCommissionId: zod.number().nullable(),
  type: zod.nativeEnum(CommissionType),
  value: zod.number(),
  organization: zod.object({
    value: zod.number(),
    label: zod.string().nullable(),
  }),
  demurrageApplied: zod.boolean().nullable(),
  extraFreightApplied: zod.boolean().nullable(),
  bafApplied: zod.boolean().nullable(),
  coaRouteId: zod.union([zod.number(), zod.string()]).nullable(),
  coaAssortmentId: zod.union([zod.number(), zod.string()]).nullable(),
});

const commissionsSchema = zod.array(commissionSchema);

const portCostSchema = zod.object({
  id: zod.number().nullable(),
  shipId: zod.object({
    value: zod.union([zod.number(), zod.nan()]),
    label: zod.string(),
  }),
  vesselTypeId: zod.object({
    value: zod.union([zod.number(), zod.nan()]),
    label: zod.string(),
  }),
  cost: zod.number(),
  locationId: zod.object({
    value: zod.number(),
    label: zod.string(),
  }),
});

const portCostsSchema = zod.array(portCostSchema);

const coaSchema = baseContractSchema
  .merge(
    zod.object({
      bunkers: bunkersSchema.optional(),
      portPairs: portPairsSchema.optional(),
      portData: portDataSchema.optional(),
      assortments: assortmentsSchema.optional(),
      prices: pricesSchema.optional(),
      vessels: vesselsSchema.optional(),
      commissions: commissionsSchema.optional(),
      portCosts: portCostsSchema.optional(),
    }),
  )
  .refine(
    (data) => {
      const portCosts = data.portCosts;
      if (portCosts) {
        for (const portCost of portCosts) {
          if (!portCost.shipId.value && !portCost.vesselTypeId.value) {
            return false;
          }
        }
      }
      return true;
    },
    {
      message: 'Either shipId or vesselTypeId must be provided',
      path: ['shipId', 'vesselTypeId'], // Optional: this will point to both fields
    },
  );

export type CoaFormFields = zod.infer<typeof coaSchema>;
type CoaFormMethods = UseFormReturn<CoaFormFields>;

const savedCoaSchema = zod.object({
  baseContract: baseContractSchema
    .omit({
      currency: true,
      shipownerOrgId: true,
      chartererOrgId: true,
      startDate: true,
      endDate: true,
      paymentType: true,
      paymentTrigger: true,
    })
    .merge(
      zod.object({
        shipownerOrgId: baseContractSchema.shape.shipownerOrgId.transform(
          (selected) => selected.value,
        ),
        chartererOrgId: baseContractSchema.shape.chartererOrgId.transform(
          (selected) => selected.value,
        ),
        startDate: dateToString,
        endDate: dateToString,
        currency: zod.nativeEnum(Currency).nullable(),
        paymentType: zod.nativeEnum(PaymentType).nullable(),
        paymentTrigger: zod.nativeEnum(PaymentTrigger).nullable(),
      }),
    ),
  bunkers: zod.array(
    bunkerSchema
      .omit({
        bafType: true,
        coaAssortmentId: true,
        coaRouteId: true,
      })
      .merge(
        zod.object({
          bafType: zod.nativeEnum(BafTypes).nullable(),
          coaAssortmentId: bunkerSchema.shape.coaAssortmentId.transform(
            (idString) => {
              if (
                idString == null ||
                (typeof idString === 'string' && idString.length < 1)
              ) {
                return null;
              }
              // If id is not a valid number it's a temporary clientId
              return isNaN(+idString) ? idString : +idString;
            },
          ),
          coaRouteId: bunkerSchema.shape.coaRouteId.transform((idString) => {
            if (
              idString == null ||
              (typeof idString === 'string' && idString.length < 1)
            ) {
              return null;
            }
            // If id is not a valid number it's a temporary clientId
            return isNaN(+idString) ? idString : +idString;
          }),
        }),
      ),
  ),
  portPairs: zod.array(
    portPairSchema
      .omit({
        id: true,
        originId: true,
        destinationId: true,
        originZoneId: true,
        destinationZoneId: true,
      })
      .merge(
        zod.object({
          id: portPairSchema.shape.id.transform((idString) => {
            // If id is not a valid number it's a temporary clientId
            return isNaN(+idString) ? idString : +idString;
          }),
          originId: portPairSchema.shape.originId.transform(
            (selected) => selected?.value ?? null,
          ),
          destinationId: portPairSchema.shape.destinationId.transform(
            (selected) => selected?.value ?? null,
          ),
          originZoneId: portPairSchema.shape.originZoneId.transform(
            (selected) => selected?.value ?? null,
          ),
          destinationZoneId: portPairSchema.shape.destinationZoneId.transform(
            (selected) => selected?.value ?? null,
          ),
        }),
      ),
  ),
  portData: portDataSaveSchema,
  assortments: zod.array(
    assortmentSchema.omit({ id: true }).merge(
      zod.object({
        id: assortmentSchema.shape.id.transform((idString) => {
          // If id is not a valid number it's a temporary clientId
          return isNaN(+idString) ? idString : +idString;
        }),
      }),
    ),
  ),
  prices: pricesSchema.transform((assortmentsByPortPair) => {
    return Object.keys(assortmentsByPortPair).flatMap((portPairKey) => {
      return Object.keys(assortmentsByPortPair[portPairKey]).map(
        (assortmentKey) => {
          const price = assortmentsByPortPair[portPairKey][assortmentKey];
          const coaAssortmentIdString = assortmentKey.slice(
            numberKeyPrefix.length,
          );
          const routeIdString = portPairKey.slice(numberKeyPrefix.length);

          const coaAssortmentId = isNaN(+coaAssortmentIdString)
            ? coaAssortmentIdString
            : +coaAssortmentIdString;

          // If routeIdString is not a valid number it's a temporary clientId
          const routeId = isNaN(+routeIdString)
            ? routeIdString
            : +routeIdString;

          return {
            ...price,
            routeId,
            coaAssortmentId,
          };
        },
      );
    });
  }),
  vessels: zod.array(
    vesselSchema.omit({ shipId: true }).merge(
      zod.object({
        shipId: vesselSchema.shape.shipId.transform(
          (selected) => selected.value,
        ),
      }),
    ),
  ),
  commissions: zod.array(
    commissionSchema
      .omit({
        coaAssortmentId: true,
        coaRouteId: true,
      })
      .merge(
        zod.object({
          coaAssortmentId: commissionSchema.shape.coaAssortmentId.transform(
            (idString) => {
              if (
                idString == null ||
                (typeof idString === 'string' && idString.length < 1)
              ) {
                return null;
              }
              // If id is not a valid number it's a temporary clientId
              return isNaN(+idString) ? idString : +idString;
            },
          ),
          coaRouteId: commissionSchema.shape.coaRouteId.transform(
            (idString) => {
              if (
                idString == null ||
                (typeof idString === 'string' && idString.length < 1)
              ) {
                return null;
              }
              // If id is not a valid number it's a temporary clientId
              return isNaN(+idString) ? idString : +idString;
            },
          ),
        }),
      ),
  ),
  portCosts: zod.array(
    portCostSchema
      .omit({
        locationId: true,
        shipId: true,
        vesselTypeId: true,
      })
      .merge(
        zod.object({
          locationId: portCostSchema.shape.locationId.transform(
            (selected) => selected.value,
          ),
          shipId: portCostSchema.shape.shipId.transform((selected) =>
            selected && !isNaN(selected.value) ? selected.value : null,
          ),
          vesselTypeId: portCostSchema.shape.vesselTypeId.transform(
            (selected) =>
              selected && !isNaN(selected.value) ? selected.value : null,
          ),
        }),
      ),
  ),
  closable: zod.boolean().optional(),
});
export type CoaFormSaveType = zod.infer<typeof savedCoaSchema>;

export interface CoaFormProps {
  coa?: Partial<CoaForm_CoaFragment>;
  onClose?: () => void;
  onSave?: (data: CoaFormSaveType) => void;
  isReadonly?: boolean;
  duplicate?: boolean;
  isLoading?: boolean;
}

const coaBunkerNodesToBunkers = (
  bunkerNodes: CoaForm_CoaBunkerFragment[],
): CoaFormFields['bunkers'] => {
  return bunkerNodes.map((bunker) => ({
    id: bunker.id,
    bunkerGrade: bunker.bunkerGrade,
    price: +bunker.price,
    basePriceMax: stringToNumOpt(bunker.basePriceMax) ?? null,
    bafStartFromBase: bunker.bafStartFromBase ?? null,
    bafStepSize: stringToNumOpt(bunker.bafStepSize) ?? null,
    bafStepEffect: stringToNumOpt(bunker.bafStepEffect) ?? null,
    bafUseStepEffectAsValue: bunker.bafUseStepEffectAsValue ?? null,
    bafBunkerAmount: stringToNumOpt(bunker.bafBunkerAmount) ?? null,
    bafType: bunker.bafType ?? null,
    coaAssortmentId: bunker.coaAssortmentId ?? null,
    coaRouteId: bunker.coaRouteId ?? null,
  }));
};

const coaPortNodesToPortPairs = (
  portPairNodes: CoaForm_PortPairFragment[],
  duplicate: boolean,
): {
  portPairs: CoaFormFields['portPairs'];
  portPairDbIdToTmpId: Map<number, string>;
} => {
  const portPairDbIdToTmpId = new Map<number, string>();

  const portPairs = portPairNodes.map((portPair) => {
    const id = duplicate ? generateClientId() : portPair.id;

    if (typeof id === 'string') {
      portPairDbIdToTmpId.set(portPair.id, id);
    }

    return {
      id,
      originId: portPair.locationByOriginId
        ? {
            value: portPair.locationByOriginId.id,
            label: portPair.locationByOriginId.name ?? '',
          }
        : null,
      destinationId: portPair.locationByDestinationId
        ? {
            value: portPair.locationByDestinationId.id,
            label: portPair.locationByDestinationId.name ?? '',
          }
        : null,
      originZoneId: portPair.zoneByOriginZoneId
        ? {
            value: portPair.zoneByOriginZoneId.id,
            label: portPair.zoneByOriginZoneId.name,
          }
        : null,
      destinationZoneId: portPair.zoneByDestinationZoneId
        ? {
            value: portPair.zoneByDestinationZoneId.id,
            label: portPair.zoneByDestinationZoneId.name,
          }
        : null,
    };
  });

  return {
    portPairs,
    portPairDbIdToTmpId,
  };
};

const coaAssortmentNodesToAssortments = (
  assortmentNodes: CoaForm_CoaAssortmentFragment[],
  duplicate: boolean,
): {
  coaAssortments: CoaFormFields['assortments'];
  coaAssortmentDbIdToTmpId: Map<number, string>;
} => {
  const coaAssortmentDbIdToTmpId = new Map<number, string>();

  const coaAssortments = assortmentNodes.map((assortment) => {
    const id = duplicate ? generateClientId() : assortment.id;

    if (typeof id === 'string') {
      coaAssortmentDbIdToTmpId.set(assortment.id, id);
    }

    return {
      id,
      assortmentId: assortment.assortmentId ?? null,
      assortmentGroupId: assortment.assortmentGroupId ?? null,
      paymentUnit: assortment.paymentUnit,
      minAmount: stringToNumOpt(assortment.minAmount) ?? null,
      maxAmount: stringToNumOpt(assortment.maxAmount) ?? null,
    };
  });

  return {
    coaAssortments,
    coaAssortmentDbIdToTmpId,
  };
};

const coaPriceNodesToPrices = (
  priceNodes: CoaForm_CoaPriceFragment[],
  duplicate: boolean,
  portPairDbIdToTmpId: Map<number, string>,
  coaAssortmentDbIdToTmpId: Map<number, string>,
): CoaFormFields['prices'] => {
  return priceNodes.reduce<NonNullable<CoaFormFields['prices']>>(
    (obj, price) => {
      if (!price.coaAssortmentId || !price.routeId) return obj;

      const routeId = duplicate
        ? portPairDbIdToTmpId.get(price.routeId)
        : price.routeId;

      const coaAssortmentId = duplicate
        ? coaAssortmentDbIdToTmpId.get(price.coaAssortmentId)
        : price.coaAssortmentId;

      if (!routeId) {
        console.error(
          'Route id not found for price',
          price,
          portPairDbIdToTmpId,
        );
        throw new Error('Price route id not found for COA edit / duplication');
      }

      if (!coaAssortmentId) {
        console.error(
          'coaAssortmentId not found for price',
          price,
          coaAssortmentDbIdToTmpId,
        );
        throw new Error(
          'Price coaAssortmentId not found for COA edit / duplication',
        );
      }

      return {
        ...obj,
        [`${numberKeyPrefix}${routeId}`]: {
          ...obj[`${numberKeyPrefix}${routeId}`],
          [`${numberKeyPrefix}${coaAssortmentId}`]: {
            id: price.id,
            price: +price.price,
            shipId: price.shipId || null,
          },
        },
      };
    },
    {},
  );
};

const coaVesselNodesToVessels = (
  vesselNodes: CoaForm_CoaVesselFragment[],
): CoaFormFields['vessels'] => {
  return vesselNodes.map((vessel) => ({
    id: vessel.id,
    shipId: {
      value: vessel.shipId,
      label: vessel.shipByShipId?.name ?? '',
    },
    demurrage: vessel.demurrage ? +vessel.demurrage : null,
  }));
};

export const parseCommissionNodes = (
  commissionNodes: CoaForm_CommissionFragment[],
) => {
  return commissionNodes
    .map((i) => ({
      ...i.commissionByCommissionId,
      id: i.commissionByCommissionId?.id ?? 0,
      type: i.commissionByCommissionId?.type ?? CommissionType.LUMPSUM,
      value: i.commissionByCommissionId?.value ?? 0,
      coaCommissionId: i.id,
      coaRouteId: i.coaRouteId ?? null,
      coaAssortmentId: i.coaAssortmentId ?? null,
      organization: {
        value: i.commissionByCommissionId?.organizationId ?? -1,
        label:
          i.commissionByCommissionId?.organizationByOrganizationId?.name ??
          null,
      },
    }))
    .filter(isDefined);
};

export const coaCommissionNodesToCommissions = (
  commissionNodes: CoaForm_CommissionFragment[],
): CoaFormFields['commissions'] => {
  return commissionNodes.map((i) => ({
    ...i,
    coaCommissionId: +i.id,
    id: i.id ?? null,
    type: i.commissionByCommissionId?.type ?? CommissionType.LUMPSUM,
    value: i.commissionByCommissionId?.value
      ? +i.commissionByCommissionId.value
      : 0,
    bafApplied: i.commissionByCommissionId?.bafApplied ?? false,
    demurrageApplied: i.commissionByCommissionId?.demurrageApplied ?? false,
    extraFreightApplied:
      i.commissionByCommissionId?.extraFreightApplied ?? false,
    coaRouteId: i.coaRouteId ?? null,
    coaAssortmentId: i.coaAssortmentId ?? null,
    organization: {
      value: i.commissionByCommissionId?.organizationId ?? -1,
      label:
        i.commissionByCommissionId?.organizationByOrganizationId?.name ?? null,
    },
  }));
};

export const coaPortCostsToPortCosts = (
  portCostNodes: CoaForm_PortCostFragment[],
): CoaFormFields['portCosts'] => {
  return portCostNodes.map((i) => ({
    id: i.id ?? null,
    cost: Number(i.cost),
    locationId: {
      value: i.locationId,
      label: i.locationByLocationId?.name ?? '',
      location: i.locationByLocationId ?? undefined,
    },
    shipId: {
      label: i.shipByShipId?.name || '',
      value: i.shipId || NaN,
    },
    vesselTypeId: {
      value: i.vesselTypeId || NaN,
      label: i.vesselAttributeByVesselTypeId?.name || '',
    },
  }));
};

export type Indexed<T> = T & { index: number };
function mapIndex<T extends Record<string, unknown>>(
  obj: T,
  index: number,
): Indexed<T> {
  return { ...obj, index };
}

const CoaForm = ({
  coa,
  isReadonly = false,
  isLoading,
  onClose,
  onSave,
  duplicate = false,
}: CoaFormProps): JSX.Element => {
  const { data, refetch } = useCoaFormQuery();

  const { data: vesselAttributeQuery } = useAllVesselAttributesQuery();
  const allVesselAttributes = useMemo(
    () => vesselAttributeQuery?.allVesselAttributes?.nodes ?? [],
    [vesselAttributeQuery],
  );

  const vesselAttributes = useMemo(() => {
    const results: { value: number; label: string }[] = [];
    allVesselAttributes.forEach((node) => {
      if (node?.id && node.name && node.isVesselClass) {
        results.push({ value: node.id, label: node.name });
      }
    });

    return results;
  }, [allVesselAttributes]);

  const defaultValues = useMemo<CoaFormFields>(() => {
    const { portPairs, portPairDbIdToTmpId } = coaPortNodesToPortPairs(
      coa?.portPairs?.nodes.filter(isDefined) ?? [],
      duplicate,
    );

    portPairs?.sort((a, b) => {
      const originLabelA = a.originId?.label ?? a.originZoneId?.label ?? '';
      const originLabelB = b.originId?.label ?? b.originZoneId?.label ?? '';
      const originComparison = originLabelA.localeCompare(originLabelB);

      if (originComparison !== 0) {
        return originComparison;
      } else {
        const destinationLabelA =
          a.destinationId?.label ?? a.destinationZoneId?.label ?? '';
        const destinationLabelB =
          b.destinationId?.label ?? b.destinationZoneId?.label ?? '';

        return destinationLabelA.localeCompare(destinationLabelB);
      }
    });

    const { coaAssortments, coaAssortmentDbIdToTmpId } =
      coaAssortmentNodesToAssortments(
        coa?.coaAssortments?.nodes.filter(isDefined) ?? [],
        duplicate,
      );

    const prices = coaPriceNodesToPrices(
      coa?.prices?.nodes.filter(isDefined) ?? [],
      duplicate,
      portPairDbIdToTmpId,
      coaAssortmentDbIdToTmpId,
    );

    const commissions = coaCommissionNodesToCommissions(
      coa?.commissions?.nodes.filter(isDefined) ?? [],
    );

    const uniqueCommissions = commissions
      ? Object.values(
          commissions.reduce<Record<string, (typeof commissions)[number]>>(
            (acc, cur) => {
              const key = `${cur.type}-${cur.value}-${cur.organization.value}`;
              acc[key] = acc[key] || cur;
              return acc;
            },
            {},
          ),
        )
      : [];

    const portCosts = coaPortCostsToPortCosts(
      coa?.portCosts?.nodes.filter(isDefined) ?? [],
    );

    return {
      shipownerOrgId: {
        value: coa?.shipownerOrgId ?? -1,
        label: coa?.organizationByShipownerOrgId?.name ?? '',
      },
      chartererOrgId: {
        value: coa?.chartererOrgId ?? -1,
        label: coa?.organizationByChartererOrgId?.name ?? '',
      },
      name: duplicate ? '' : (coa?.name ?? ''),
      startDate: coa?.startDate ? new Date(coa.startDate) : new Date(),
      endDate: coa?.endDate ? new Date(coa.endDate) : new Date(),
      minAmount: coa?.minAmount ? +coa.minAmount : null,
      maxAmount: coa?.maxAmount ? +coa.maxAmount : null,
      amountUnitId: coa?.amountUnitId ?? null,
      currency: coa?.currency ?? null,
      paymentDays: coa?.paymentDays ? +coa.paymentDays : null,
      paymentTrigger: coa?.paymentTrigger ?? null,
      paymentType: coa?.paymentType ?? null,
      externalId: coa?.externalId ?? null,
      bunkers: coaBunkerNodesToBunkers(
        coa?.bunkers?.nodes.filter(isDefined) ?? [],
      ),
      portPairs,
      portData: portDataNodesToPortData(
        coa?.portData?.nodes
          .map((pd) => pd?.portDatumByPortDataId)
          .filter(isDefined) ?? [],
      ),
      assortments: coaAssortments,
      prices,
      vessels: coaVesselNodesToVessels(
        coa?.vessels?.nodes.filter(isDefined) ?? [],
      ),
      commissions: uniqueCommissions,
      portCosts,
    };
  }, [coa, duplicate]);

  const form = useForm<CoaFormFields>({
    resolver: zodResolver(coaSchema),
    reValidateMode: 'onSubmit',
    defaultValues,
  });

  const organizationOptions = nodesToOptions(
    data?.allOrganizations?.nodes ?? [],
  );

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    register,
  } = form;

  const errorKeys = Object.keys(errors);

  const {
    fields: portPairFields,
    append: appendPortPair,
    remove: removePortPair,
  } = useFieldArray({
    control,
    name: 'portPairs',
    keyName: 'key',
  });

  const {
    fields: portCostFields,
    append: appendPortCost,
    remove: removePortCost,
  } = useFieldArray({
    control,
    name: 'portCosts',
    keyName: 'key',
  });

  const portPairs = (watch('portPairs', portPairFields) ?? [])
    .map(mapIndex)
    .filter(
      (portPair) =>
        portPair.originId?.value !== 0 && portPair.destinationId?.value !== 0,
    );

  const assortmentFieldMethods = useFieldArray({
    control,
    name: 'assortments',
    keyName: 'key',
  });

  const {
    fields: commissionFields,
    append: appendCommission,
    remove: removeCommission,
  } = useFieldArray({
    control,
    name: 'commissions',
    keyName: 'key',
  });

  const assortmentById = useMemo(
    () => mapKeyBy(compact(data?.allAssortments?.nodes), (node) => node.id),
    [data?.allAssortments],
  );

  const assortmentGroupById = useMemo(
    () =>
      mapKeyBy(compact(data?.allAssortmentGroups?.nodes), (node) => node.id),
    [data?.allAssortmentGroups],
  );

  const zones = useMemo(() => {
    return compact(data?.allZones?.nodes).map((zone) => {
      return {
        id: zone.id,
        name: zone.name,
        locationIds: new Set(
          compact(zone.zoneLocationsByZoneId.nodes).map(
            (zoneLocation) => zoneLocation?.locationId,
          ),
        ),
      };
    });
  }, [data?.allZones?.nodes]);

  const onFormSubmit = (closable: boolean) => (data: CoaFormFields) => {
    const {
      amountUnitId,
      currency,
      endDate,
      maxAmount,
      minAmount,
      name,
      paymentDays,
      paymentTrigger,
      paymentType,
      shipownerOrgId,
      chartererOrgId,
      startDate,
      bunkers,
      portPairs,
      portData,
      assortments,
      prices,
      vessels,
      commissions,
      externalId,
      portCosts,
    } = data;

    try {
      const dataToSave = savedCoaSchema.parse({
        baseContract: {
          amountUnitId,
          currency,
          endDate,
          maxAmount,
          minAmount,
          name,
          paymentDays,
          paymentTrigger,
          paymentType,
          shipownerOrgId: shipownerOrgId,
          chartererOrgId,
          startDate,
          externalId,
        },
        bunkers: bunkers ?? [],
        portPairs: portPairs ?? [],
        portData: portData ?? {},
        assortments: assortments ?? [],
        prices: prices ?? {},
        vessels: vessels ?? [],
        commissions: commissions ?? [],
        portCosts: portCosts ?? [],
        closable,
      });

      onSave?.(dataToSave);
      if (closable) onClose?.();
    } catch (error) {
      console.error('Error parsing coa data to save', error);
    }
  };

  return !data ? (
    <></>
  ) : (
    <Box display="flex" flexDirection="column" height="100%" flexGrow={1}>
      <Form
        width="100%"
        overflowY="scroll"
        onSubmit={handleSubmit(onFormSubmit(false))}
      >
        <BaseContract data={data} form={form} isReadonly={isReadonly} />
        <CommissionFields
          fields={commissionFields}
          append={appendCommission}
          remove={removeCommission}
          refetch={refetch}
          control={control}
          organizationOptions={organizationOptions}
          setValue={setValue}
          register={register}
          portPairs={portPairs}
          assortmentFieldMethods={assortmentFieldMethods}
          assortmentById={assortmentById}
          assortmentGroupById={assortmentGroupById}
          watch={watch}
        />
        <Assortments
          data={data}
          form={form}
          assortmentFieldMethods={assortmentFieldMethods}
          isReadonly={isReadonly}
          portPairs={portPairs}
          assortmentById={assortmentById}
          assortmentGroupById={assortmentGroupById}
        />
        <PortPairs
          data={data}
          form={form}
          fields={portPairFields}
          append={appendPortPair}
          remove={removePortPair}
          isReadonly={isReadonly}
          zones={zones}
        />
        <Bunkers
          data={data}
          form={form}
          isReadonly={isReadonly}
          portPairs={portPairs}
          assortmentFieldMethods={assortmentFieldMethods}
          assortmentById={assortmentById}
          assortmentGroupById={assortmentGroupById}
        />
        <PortData
          defaultValues={defaultValues.portData}
          data={data}
          form={form}
          portPairs={portPairs}
          isReadonly={isReadonly}
        />
        <FreightRates
          data={data}
          form={form}
          portPairs={portPairs}
          assortmentFieldMethods={assortmentFieldMethods}
          assortmentById={assortmentById}
          assortmentGroupById={assortmentGroupById}
          isReadonly={isReadonly}
        />
        <PortCosts
          data={data}
          form={form}
          fields={portCostFields}
          append={appendPortCost}
          remove={removePortCost}
          isReadonly={isReadonly}
          portPairs={portPairs}
          vesselAttributes={vesselAttributes}
        />
      </Form>
      {!isReadonly ? (
        <Box>
          {errorKeys.length > 0 && (
            <Box bg="neutral.white_50" p={4}>
              <Text color="#9d0300">
                {errorKeys
                  .map((key) => `${startCase(key)} is not valid.`)
                  .join(' ')}
              </Text>
            </Box>
          )}
          <ModalFooter>
            <Button variant="negative" onPress={() => onClose?.()} mr={2}>
              Cancel
            </Button>
            <Button
              disabled={isLoading}
              variant="secondary"
              onPress={handleSubmit(onFormSubmit(false))}
              mr={2}
            >
              Save
            </Button>
            <Button
              disabled={isLoading}
              onPress={handleSubmit(onFormSubmit(true))}
              data-testid="save-and-close"
            >
              {'Save & Close'}
            </Button>
            <div style={{ flexGrow: 1 }} />
          </ModalFooter>
        </Box>
      ) : null}
    </Box>
  );
};

interface CoaFormSectionProps {
  data: CoaFormQueryResult['data'];
  form: CoaFormMethods;
  isReadonly: boolean;
}

const BaseContract = ({ data, form, isReadonly }: CoaFormSectionProps) => {
  const currentUser = useCurrentUser();
  const { refetch } = useCoaFormQuery();

  const [
    createCharterer,
    { loading: createChartererLoading, error: createChartererError },
  ] = useCreateChartererMutation();

  const {
    control,
    register,
    setValue,
    formState: { errors },
  } = form;

  const organisationId = currentUser?.organizationId ?? null;
  const handleCreateCharterer = useCallback(
    async (inputValue: string) => {
      const { data } = await createCharterer({
        variables: {
          input: {
            organization: {
              name: inputValue,
              charterer: true,
              parentOrganizationId: organisationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };
      if (id !== undefined) {
        setValue('chartererOrgId', { value: id, label: name ?? inputValue });
      }
      await refetch();
    },
    [createCharterer, refetch, setValue, organisationId],
  );

  useEffect(() => {
    if (createChartererError) {
      toast.error(`Error creating charterer: ${createChartererError.message}`);
    }
  }, [createChartererError]);

  const shipownerOptions = nodesToOptions(data?.shipowners?.nodes ?? []);
  const chartererOptions = nodesToOptions(data?.charterers?.nodes ?? []);

  const shouldUseReducedUnits =
    currentUser?.rootOrganization?.configReducedUnits ?? false;
  const cargoUnitOptions = nodesToOptions(
    data?.allCargoUnits?.nodes ?? [],
  ).filter((unit) => {
    if (shouldUseReducedUnits) {
      return reducedUnits.includes(unit.label);
    }
    return unit;
  });

  return (
    <FormSection title="Contract basics" muted>
      <FormRow>
        <FormField
          label="Charterer"
          width={200}
          required
          hasError={!!errors.chartererOrgId || !!createChartererError}
          data-testid="charterer"
        >
          <Controller
            name="chartererOrgId"
            control={control}
            render={({ field }) => (
              <CreatableSelect
                isLoading={createChartererLoading}
                isDisabled={createChartererLoading}
                styles={reactSelectStyles}
                options={chartererOptions}
                onCreateOption={handleCreateCharterer}
                {...field}
              />
            )}
          />
        </FormField>
        <FormField
          label="Shipowner"
          width={200}
          required
          hasError={!!errors.shipownerOrgId}
          data-testid="shipowner"
        >
          <Controller
            name="shipownerOrgId"
            control={control}
            render={({ field }) => (
              <ReactSelect
                styles={reactSelectStyles}
                options={shipownerOptions}
                {...field}
              />
            )}
          />
        </FormField>
        <FormField
          label="Contract name"
          width={200}
          required
          hasError={!!errors.name}
        >
          <Input
            {...register('name')}
            readOnly={isReadonly}
            data-testid="name"
          />
        </FormField>
        <FormField
          label="Start date"
          width={200}
          required
          hasError={!!errors.startDate}
        >
          <Controller
            defaultValue={undefined}
            control={control}
            name="startDate"
            render={({ field: { value, onChange } }) => (
              <DatePicker
                onChange={onChange}
                selected={value ? value : null}
                showTime={false}
                timeZone="UTC"
              />
            )}
          />
        </FormField>
        <FormField
          label="End date"
          width={200}
          required
          hasError={!!errors.endDate}
        >
          <Controller
            defaultValue={undefined}
            control={control}
            name="endDate"
            render={({ field: { value, onChange } }) => (
              <DatePicker
                onChange={onChange}
                selected={value ? value : null}
                showTime={false}
                timeZone="UTC"
              />
            )}
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField
          label="Minimum quantity"
          width={200}
          hasError={!!errors.minAmount}
        >
          <Input
            type="number"
            {...register('minAmount', { setValueAs: stringToNumber })}
            readOnly={isReadonly}
          />
        </FormField>
        <FormField
          label="Maximum quantity"
          width={200}
          hasError={!!errors.maxAmount}
        >
          <Input
            type="number"
            {...register('maxAmount', { setValueAs: stringToNumber })}
            readOnly={isReadonly}
          />
        </FormField>
        <FormField label="Unit" width={200}>
          <Select
            {...register('amountUnitId', { setValueAs: stringToNumber })}
            options={cargoUnitOptions}
          />
        </FormField>
        <FormField
          label="Contract currency"
          width={200}
          hasError={!!errors.currency}
        >
          <Select
            {...register('currency', {
              setValueAs: valueOrNull,
            })}
            options={paymentUnitsToOptions(currencies)}
            placeholder="-"
            enablePlaceholder
          />
        </FormField>
      </FormRow>
      <FormRow>
        <FormField
          label="Payment days"
          width={200}
          hasError={!!errors.paymentDays}
        >
          <Input
            type="number"
            {...register('paymentDays', {
              setValueAs: stringToNumber,
            })}
            readOnly={isReadonly}
          />
        </FormField>
        <FormField
          label="Payment trigger"
          width={200}
          hasError={!!errors.paymentTrigger}
        >
          <Select
            {...register('paymentTrigger', {
              setValueAs: valueOrNull,
            })}
            options={paymentUnitsToOptions(paymentTriggers)}
            placeholder="-"
            enablePlaceholder
          />
        </FormField>
        <FormField
          label="Payment terms"
          width={200}
          hasError={!!errors.paymentType}
        >
          <Select
            {...register('paymentType', {
              setValueAs: valueOrNull,
            })}
            options={paymentUnitsToOptions(paymentTypes)}
            placeholder="-"
            enablePlaceholder
          />
        </FormField>
        <FormField
          label="External ID"
          width={200}
          hasError={!!errors.externalId}
        >
          <Input
            type="text"
            {...register('externalId')}
            readOnly={isReadonly}
          />
        </FormField>
      </FormRow>
    </FormSection>
  );
};

const bunkerGradeOptions = Object.values(BunkerGrade).map((bunkerGrade) => ({
  value: bunkerGrade,
  label: bunkerGrade,
}));

const bafTypes: Record<BafTypes | '', string> = {
  '': 'None',
  ACTUAL_BUNKERS_USED: 'Actual bunkers used',
  AGREED_BUNKER_AMOUNT: 'Agreed bunker amount',
  STEPPED: 'Stepped',
};

const bafTypeOptions = Object.entries(bafTypes).map(([value, label]) => ({
  value,
  label,
}));

const Bunkers = ({
  form,
  isReadonly,
  portPairs,
  assortmentFieldMethods,
  assortmentById,
  assortmentGroupById,
}: {
  assortmentFieldMethods: UseFieldArrayReturn<
    CoaFormFields,
    'assortments',
    'key'
  >;
  assortmentById: Map<number, CoaForm_AssortmentFragment>;
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>;
  portPairs: PortPairType[];
} & CoaFormSectionProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = form;
  const {
    fields: bunkerFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'bunkers',
    keyName: 'key',
  });

  const watchedBunkers = bunkerFields.length
    ? (watch('bunkers', bunkerFields as BunkerType[]) ?? [])
    : [];

  const bafTypeByBunkerIndex = new Map<number, BafTypes | null>(
    watchedBunkers.map((coaBunker, index) => [
      index,
      coaBunker.bafType ? BafTypes[coaBunker.bafType as BafTypes] : null,
    ]),
  );

  const { fields: assortmentFields } = assortmentFieldMethods;

  const watchedAssortments = assortmentFields.length
    ? (watch('assortments', assortmentFields as AssortmentType[]) ?? [])
    : [];

  const assortmentOptions = [
    { value: '', label: 'Apply to all commodities' },
    ...watchedAssortments
      .map(mapAssortmentName(assortmentById, assortmentGroupById))
      .sort(sortByAssortmentName)
      .map((field) => ({
        value: field.id,
        label: `${
          field.assortmentName ??
          field.assortmentGroupName ??
          'Commodity name N/A'
        } ${field.minAmount ?? ''} - ${field.maxAmount ?? ''}`,
      })),
  ];

  const routeOptions = [
    { value: '', label: 'Apply to all routes' },
    ...portPairs.map((portPair) => ({
      value: portPair.id,
      label: `${portPair.originId?.label} - ${portPair.destinationId?.label}`,
    })),
  ];

  return (
    <FormSection title="Bunker grades/prices">
      {bunkerFields.length > 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          borderColor="neutral.gray_225"
          borderWidth={1}
          borderStyle="solid"
          mb={4}
        >
          {bunkerFields.map((field, index) => (
            <Box
              key={field.key}
              bg={index % 2 === 0 ? 'neutral.white_50' : 'neutral.white_100'}
              borderTopColor="neutral.gray_225"
              borderTopWidth={index === 0 ? 0 : 1}
              borderTopStyle="solid"
              p={4}
              width="100%"
            >
              <FormRow>
                <Input
                  type="hidden"
                  {...register(`bunkers.${index}.id`)}
                  defaultValue={field.id || undefined}
                />
                <FormField
                  label="Bunker grade"
                  width={200}
                  hasError={!!errors.bunkers?.[index]?.bunkerGrade}
                >
                  <Select
                    options={bunkerGradeOptions}
                    {...register(`bunkers.${index}.bunkerGrade`)}
                    defaultValue={field.bunkerGrade}
                    data-testid={`bunker-${index}-grade`}
                  />
                </FormField>
                <FormField
                  label="Base price (min.)"
                  width={200}
                  required
                  hasError={!!errors.bunkers?.[index]?.price}
                >
                  <Input
                    type="number"
                    min="0"
                    {...register(`bunkers.${index}.price`, {
                      setValueAs: stringToNumber,
                    })}
                    defaultValue={field.price || undefined}
                    readOnly={isReadonly}
                    data-testid={`bunker-${index}-price`}
                  />
                </FormField>
                <FormField
                  label="Base price (max.)"
                  width={200}
                  hasError={!!errors.bunkers?.[index]?.basePriceMax}
                >
                  <Input
                    type="number"
                    min="0"
                    {...register(`bunkers.${index}.basePriceMax`, {
                      setValueAs: stringToNumber,
                    })}
                    defaultValue={field.basePriceMax || undefined}
                    readOnly={isReadonly}
                    data-testid={`bunker-${index}-base-price-max`}
                  />
                </FormField>
                <FormField
                  label="Commodity"
                  width={200}
                  hasError={!!errors.bunkers?.[index]?.coaAssortmentId}
                >
                  <Select
                    {...register(`bunkers.${index}.coaAssortmentId`)}
                    defaultValue={field.coaAssortmentId || undefined}
                    options={assortmentOptions}
                    data-testid={`bunker-${index}-assortment`}
                  />
                </FormField>
                <FormField
                  label="Route"
                  width={200}
                  hasError={!!errors.bunkers?.[index]?.coaRouteId}
                >
                  <Select
                    {...register(`bunkers.${index}.coaRouteId`)}
                    defaultValue={field.coaRouteId || undefined}
                    options={routeOptions}
                    data-testid={`bunker-${index}-route`}
                  />
                </FormField>
              </FormRow>
              <FormRow>
                <FormField
                  label="BAF type"
                  width={200}
                  hasError={!!errors.bunkers?.[index]?.bafType}
                >
                  <Select
                    {...register(`bunkers.${index}.bafType`)}
                    defaultValue={field.bafType || undefined}
                    options={bafTypeOptions}
                    data-testid={`bunker-${index}-baf-type`}
                  />
                </FormField>
                <Box
                  display={
                    bafTypeByBunkerIndex.get(index) === BafTypes.STEPPED
                      ? 'inline-flex'
                      : 'none'
                  }
                >
                  <FormField
                    label="BAF step size"
                    width={200}
                    hasError={!!errors.bunkers?.[index]?.bafStepSize}
                  >
                    <Input
                      type="number"
                      min="0"
                      {...register(`bunkers.${index}.bafStepSize`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.bafStepSize || undefined}
                      readOnly={isReadonly}
                      data-testid={`bunker-${index}-baf-step-size`}
                    />
                  </FormField>
                  <FormField
                    label="BAF step effect"
                    width={200}
                    hasError={!!errors.bunkers?.[index]?.bafStepEffect}
                  >
                    <Input
                      type="number"
                      min="0"
                      {...register(`bunkers.${index}.bafStepEffect`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.bafStepEffect || undefined}
                      readOnly={isReadonly}
                      data-testid={`bunker-${index}-baf-step-effect`}
                    />
                  </FormField>
                  <FormField
                    label="BAF Options"
                    width={400}
                    hasError={!!errors.bunkers?.[index]?.bafStartFromBase}
                  >
                    <label>
                      <input
                        type="checkbox"
                        {...register(`bunkers.${index}.bafStartFromBase`)}
                        defaultChecked={field.bafStartFromBase ?? undefined}
                        readOnly={isReadonly}
                        data-testid={`bunker-${index}-baf-start-from-base`}
                      />
                      Start BAF from base
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        {...register(
                          `bunkers.${index}.bafUseStepEffectAsValue`,
                        )}
                        defaultChecked={
                          field.bafUseStepEffectAsValue ?? undefined
                        }
                        readOnly={isReadonly}
                        data-testid={`bunker-${index}-baf-use-step-effect-as-value`}
                      />
                      Use effect as value instead of %
                    </label>
                  </FormField>
                </Box>
                <Box
                  display={
                    bafTypeByBunkerIndex.get(index) ===
                    BafTypes.AGREED_BUNKER_AMOUNT
                      ? 'inline-flex'
                      : 'none'
                  }
                >
                  <FormField
                    label="Agreed bunker in tons"
                    width={200}
                    hasError={!!errors.bunkers?.[index]?.bafBunkerAmount}
                  >
                    <Input
                      type="number"
                      min="0"
                      {...register(`bunkers.${index}.bafBunkerAmount`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.bafBunkerAmount || undefined}
                      readOnly={isReadonly}
                      data-testid={`bunker-${index}-baf-bunker-amount`}
                    />
                  </FormField>
                </Box>
              </FormRow>
              {!isReadonly && (
                <FormRow>
                  <Box display="flex" pb={4}>
                    <Button variant="secondary" onPress={() => remove(index)}>
                      Delete
                    </Button>
                  </Box>
                </FormRow>
              )}
            </Box>
          ))}
        </Box>
      )}
      <Box>
        {!isReadonly && (
          <Button
            variant="secondary"
            onPress={() => {
              append({
                bunkerGrade: BunkerGrade.MGO,
                price: NaN,
                id: null,
                basePriceMax: null,
                bafType: null,
                bafStepSize: null,
                bafStepEffect: null,
                bafStartFromBase: null,
                bafUseStepEffectAsValue: null,
                bafBunkerAmount: null,
                coaRouteId: null,
                coaAssortmentId: null,
              });
            }}
            data-testid="add-bunker"
          >
            + Add bunker grade/price
          </Button>
        )}
      </Box>
    </FormSection>
  );
};

const PortPairs = ({
  append,
  fields,
  form,
  remove,
  isReadonly,
  zones,
}: {
  append: UseFieldArrayAppend<CoaFormFields, 'portPairs'>;
  fields: Partial<FieldArrayWithId<CoaFormFields, 'portPairs', 'key'>>[];
  remove: (index?: number | number[] | undefined) => void;
  zones: { id: number; name: string; locationIds: Set<number> }[];
} & CoaFormSectionProps) => {
  const {
    control,
    register,
    unregister,
    formState: { errors },
    setValue,
  } = form;

  const removePortPairHandler = useCallback(
    (index: number, portPairId: number | string | undefined) => {
      unregister(`prices.id-${portPairId}`);
      remove(index);
    },
    [remove, unregister],
  );

  const onLocationZoneChange = useCallback(
    (
      field: 'from' | 'to',
      index: number,
      newValue: LocationZoneSelectValue | undefined,
    ) => {
      const newLocationValue =
        newValue && newValue.type === 'location'
          ? {
              label: newValue.label,
              value: newValue.value,
            }
          : null;
      const newZoneValue =
        newValue && newValue.type === 'zone'
          ? {
              label: newValue.label,
              value: newValue.value,
            }
          : null;

      if (field === 'from') {
        setValue(`portPairs.${index}.originId`, newLocationValue);
        setValue(`portPairs.${index}.originZoneId`, newZoneValue);
      }

      if (field === 'to') {
        setValue(`portPairs.${index}.destinationId`, newLocationValue);
        setValue(`portPairs.${index}.destinationZoneId`, newZoneValue);
      }
    },
    [setValue],
  );

  function getPortPairSelectValue(
    field: PortPairType | undefined,
    prop: 'from' | 'to',
  ): LocationZoneSelectValue | undefined {
    if (prop === 'from') {
      if (!field?.originId && !field?.originZoneId) return undefined;
      if (field.originId) {
        return {
          type: 'location',
          label: field.originId.label,
          value: field.originId.value,
        };
      }
      if (field.originZoneId) {
        return {
          type: 'zone',
          label: field.originZoneId.label,
          value: field.originZoneId.value,
        };
      }
    }

    if (prop === 'to') {
      if (!field?.destinationId && !field?.destinationZoneId) return undefined;
      if (field.destinationId) {
        return {
          type: 'location',
          label: field.destinationId.label,
          value: field.destinationId.value,
        };
      }
      if (field.destinationZoneId) {
        return {
          type: 'zone',
          label: field.destinationZoneId.label,
          value: field.destinationZoneId.value,
        };
      }
    }
  }

  const watchedValues = useWatch({
    control,
    name: `portPairs`,
  });

  const locationsByZoneId = useMemo(() => {
    return new Map(zones.map((zone) => [zone.id, zone.locationIds]));
  }, [zones]);

  const ignoredZoneIds = useMemo(() => {
    const zoneIds = new Set<number>();

    watchedValues?.forEach((portPair) => {
      const origin = portPair.originZoneId?.value;
      if (origin) {
        zoneIds.add(origin);
      }
      const destination = portPair.destinationZoneId?.value;
      if (destination) {
        zoneIds.add(destination);
      }
    });

    const allZoneLocations = new Set(
      Array.from(zoneIds).flatMap((zoneId) =>
        Array.from(locationsByZoneId.get(zoneId) ?? []),
      ),
    );

    return new Set(
      zones
        .filter(
          (zone) =>
            !zoneIds.has(zone.id) &&
            setIntersection([zone.locationIds, allZoneLocations]).size > 0,
        )
        .map((zone) => zone.id),
    );
  }, [locationsByZoneId, watchedValues, zones]);

  return (
    <FormSection title="Port pairs" muted>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {fields.map((portPair, index) => {
          const originZone =
            watchedValues?.at(index)?.originZoneId?.value ?? null;
          const destinationZone =
            watchedValues?.at(index)?.destinationZoneId?.value ?? null;

          return (
            <FormRow key={portPair.key}>
              <Input
                type="hidden"
                {...register(`portPairs.${index}.id`)}
                defaultValue={portPair.id}
                readOnly={isReadonly}
              />
              <FormField
                label={index === 0 ? 'From' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portPairs?.[index]?.originId}
                data-testid={`from-location-${index}`}
              >
                <LocationZoneSelect
                  value={getPortPairSelectValue(
                    watchedValues?.at(index),
                    'from',
                  )}
                  onChange={(val) => onLocationZoneChange('from', index, val)}
                  skipLocationIds={
                    destinationZone
                      ? locationsByZoneId.get(destinationZone)
                      : undefined
                  }
                  skipZoneIds={ignoredZoneIds}
                  skipInvalidLocations={false}
                />
              </FormField>
              <FormField
                label={index === 0 ? 'To' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portPairs?.[index]?.destinationId}
                data-testid={`to-location-${index}`}
              >
                <LocationZoneSelect
                  value={getPortPairSelectValue(watchedValues?.at(index), 'to')}
                  onChange={(val) => onLocationZoneChange('to', index, val)}
                  skipLocationIds={
                    originZone ? locationsByZoneId.get(originZone) : undefined
                  }
                  skipZoneIds={ignoredZoneIds}
                  skipInvalidLocations={false}
                />
              </FormField>
              {!isReadonly && (
                <Box alignSelf="flex-end" pb={3}>
                  <DeleteButton
                    onPress={() => {
                      removePortPairHandler(index, portPair.id);
                    }}
                  />
                </Box>
              )}
            </FormRow>
          );
        })}
        {!isReadonly && (
          <Button
            variant="secondary"
            onPress={() =>
              append({
                id: generateClientId(),
                destinationId: null,
                destinationZoneId: null,
                originId: null,
                originZoneId: null,
              })
            }
            mt={3}
            data-testid="add-port-pair"
          >
            + Add port pair
          </Button>
        )}
      </Box>
    </FormSection>
  );
};

const PortData = ({
  defaultValues,
  form,
  portPairs,
  isReadonly,
  data,
}: {
  defaultValues: CoaFormFields['portData'];
  portPairs: PortPairType[];
} & CoaFormSectionProps) => {
  const locationById = useMemo(
    () =>
      portPairs.reduce<Map<number, { id: number; name: string }>>(
        (map, portPair) => {
          if (portPair.originId?.value && !map.has(portPair.originId?.value))
            map.set(portPair.originId.value, {
              id: portPair.originId.value,
              name: portPair.originId.label,
            });
          if (
            portPair.destinationId?.value &&
            !map.has(portPair.destinationId?.value)
          )
            map.set(portPair.destinationId.value, {
              id: portPair.destinationId.value,
              name: portPair.destinationId.label,
            });
          return map;
        },
        new Map(),
      ),
    [portPairs],
  );

  const portAgents = useMemo(
    () => data?.portAgents?.nodes.filter(isDefined) ?? [],
    [data?.portAgents],
  );

  return (
    <FormProvider {...form}>
      <PortDataForm
        defaultValues={defaultValues ?? {}}
        isReadonly={isReadonly}
        portAgents={portAgents}
        locationById={locationById}
      />
    </FormProvider>
  );
};

const getPriceField = (
  portPairId: number | string,
  coaAssortmentId: number | string,
  field: keyof PriceType,
): FieldPath<CoaFormFields> =>
  `prices.${numberKeyPrefix}${portPairId}.${numberKeyPrefix}${coaAssortmentId}.${field}`;

const fixedTableLayout: React.CSSProperties = { tableLayout: 'fixed' };

function mapAssortmentName(
  assortmentById: Map<number, CoaForm_AssortmentFragment>,
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>,
) {
  return function <
    T extends {
      assortmentId?: number | null | string;
      assortmentGroupId?: number | null | string;
    },
  >(
    assortmentField: T,
  ): T & { assortmentName: string | null; assortmentGroupName: string | null } {
    const assortmentName =
      assortmentField?.assortmentId != null
        ? (assortmentById.get(+assortmentField.assortmentId)?.name ?? null)
        : null;

    const assortmentGroupName =
      assortmentField?.assortmentGroupId != null
        ? (assortmentGroupById.get(+assortmentField.assortmentGroupId)?.name ??
          null)
        : null;

    return {
      ...assortmentField,
      assortmentName,
      assortmentGroupName,
    };
  };
}

function sortByAssortmentName<T extends { assortmentName: string | null }>(
  { assortmentName: a }: T,
  { assortmentName: b }: T,
): number {
  if (a === b) return 0;

  if (!a) return 1;
  if (!b) return -1;

  return a.localeCompare(b);
}

const Assortments = ({
  isReadonly,
  form,
  assortmentFieldMethods,
  assortmentById,
  assortmentGroupById,
  portPairs,
}: {
  assortmentFieldMethods: UseFieldArrayReturn<
    CoaFormFields,
    'assortments',
    'key'
  >;
  assortmentById: Map<number, CoaForm_AssortmentFragment>;
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>;
  portPairs: PortPairType[];
} & CoaFormSectionProps) => {
  const { register, unregister } = form;

  const {
    fields: assortmentFields,
    append: appendAssortment,
    remove: removeAssortment,
  } = assortmentFieldMethods;

  const sortedAssortmentFields = assortmentFields
    .map(mapIndex)
    .map(mapAssortmentName(assortmentById, assortmentGroupById))
    .sort(sortByAssortmentName);

  const removeAssortmentHandler = useCallback(
    (index: number, assortmentId: number | string) => {
      for (const portPair of portPairs) {
        unregister(`prices.id-${portPair.id}.id-${assortmentId}`);
      }
      removeAssortment(index);
    },
    [portPairs, removeAssortment, unregister],
  );

  return (
    <FormSection title="Commodities">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-start"
        mb={3}
        borderWidth={1}
        borderStyle="solid"
        borderColor="neutral.gray_225"
      >
        <Table style={fixedTableLayout}>
          <thead>
            <TableHeaders>
              <TableHeader width={200}>Commodity</TableHeader>
              <TableHeader width={200}>Lot min. quantity</TableHeader>
              <TableHeader width={200}>Lot max. quantity</TableHeader>
              <TableHeader width={200}>Actions</TableHeader>
            </TableHeaders>
          </thead>
          <tbody>
            {sortedAssortmentFields.map((field, index) => {
              const assortment = field.assortmentId
                ? assortmentById.get(+field.assortmentId)
                : null;
              const assortmentGroup = field.assortmentGroupId
                ? assortmentGroupById.get(+field.assortmentGroupId)
                : null;

              return (
                <TableRow key={field.key} isMuted={index % 2 !== 0}>
                  <TableCell data-testid={`assortment-${index}`}>
                    <input
                      type="hidden"
                      {...register(`assortments.${field.index}.id`)}
                      defaultValue={field.id ?? ''}
                      readOnly={isReadonly}
                    />
                    <input
                      type="hidden"
                      {...register(`assortments.${field.index}.assortmentId`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.assortmentId ?? ''}
                      readOnly={isReadonly}
                    />
                    <input
                      type="hidden"
                      {...register(
                        `assortments.${field.index}.assortmentGroupId`,
                        {
                          setValueAs: stringToNumber,
                        },
                      )}
                      defaultValue={field.assortmentGroupId ?? ''}
                      readOnly={isReadonly}
                    />
                    {assortment
                      ? assortment.name
                      : assortmentGroup
                        ? assortmentGroup.name
                        : 'Undefined commodity'}
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      {...register(`assortments.${field.index}.minAmount`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.minAmount ?? undefined}
                      readOnly={isReadonly}
                      data-testid={`assortment-${index}-min-amount`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      {...register(`assortments.${field.index}.maxAmount`, {
                        setValueAs: stringToNumber,
                      })}
                      defaultValue={field.maxAmount ?? undefined}
                      readOnly={isReadonly}
                      data-testid={`assortment-${index}-max-amount`}
                    />
                  </TableCell>
                  <TableCell>
                    {!isReadonly && (
                      <Box alignSelf="flex-end" pb={3}>
                        <DeleteButton
                          onPress={() => {
                            removeAssortmentHandler(field.index, field.id);
                          }}
                        />
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </Box>
      <Box>
        {!isReadonly && (
          <NewAssortmentForm
            assortmentById={assortmentById}
            assortmentGroupById={assortmentGroupById}
            onAdd={(newAssortment) =>
              appendAssortment({
                id: generateClientId(),
                assortmentId: newAssortment.assortmentId,
                assortmentGroupId: newAssortment.assortmentGroupId,
                paymentUnit: PaymentUnit.MT,
                minAmount: null,
                maxAmount: null,
              })
            }
          />
        )}
      </Box>
    </FormSection>
  );
};

const NewAssortmentForm = ({
  assortmentById,
  assortmentGroupById,
  onAdd,
}: {
  assortmentById: Map<number, CoaForm_AssortmentFragment>;
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>;
  onAdd: (newAssortment: {
    assortmentId: number | null;
    assortmentGroupId: number | null;
  }) => void;
}) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const assortmentOptions = useMemo(() => {
    const options: {
      assortmentId: number | null;
      assortmentGroupId: number | null;
      value: string;
      label: string;
    }[] = [
      {
        assortmentId: null,
        assortmentGroupId: null,
        value: '0-0',
        label: 'Undefined commodity',
      },
    ];

    assortmentById.forEach((assortment) => {
      const groupName = assortment.assortmentGroup
        ? (assortmentGroupById.get(assortment.assortmentGroup)?.name ??
          'Group N/A')
        : 'Uncategorized';

      options.push({
        assortmentId: assortment.id,
        assortmentGroupId: null,
        value: `${assortment.assortmentGroup ?? '0'}-${assortment.id}`,
        label: `${groupName} » ${assortment.name}`,
      });
    });

    assortmentGroupById.forEach((assortmentGroup) => {
      options.push({
        assortmentId: null,
        assortmentGroupId: assortmentGroup.id,
        value: `${assortmentGroup.id}-0`,
        label: `${assortmentGroup.name}`,
      });
    });

    options.sort((a, b) => a.value.localeCompare(b.value));

    return options;
  }, [assortmentById, assortmentGroupById]);

  const selectedOption =
    assortmentOptions.find((opt) => opt.value === selectedValue) ?? null;

  const handleAddPress = () => {
    if (!selectedOption) return;

    onAdd({
      assortmentId: selectedOption.assortmentId,
      assortmentGroupId: selectedOption.assortmentGroupId,
    });

    setSelectedValue(null);
  };

  return (
    <Box>
      <FormRow>
        <FormField
          label="Commodity"
          width={300}
          data-testid="add-assortment-name"
        >
          <ReactSelect
            isClearable
            styles={reactSelectStyles}
            options={assortmentOptions}
            value={selectedOption}
            onChange={(val: { value: string; label: string }) =>
              setSelectedValue(val?.value ?? null)
            }
          />
        </FormField>
        <FormField width={200}>
          <Button
            variant="secondary"
            disabled={selectedValue === null}
            onPress={handleAddPress}
            data-testid="add-assortment"
          >
            + Add commodity
          </Button>
        </FormField>
      </FormRow>
    </Box>
  );
};

const FreightRates = ({
  data,
  form,
  portPairs,
  assortmentFieldMethods,
  assortmentById,
  assortmentGroupById,
  isReadonly,
}: {
  portPairs: PortPairType[];
  assortmentFieldMethods: UseFieldArrayReturn<
    CoaFormFields,
    'assortments',
    'key'
  >;
  assortmentById: Map<number, CoaForm_AssortmentFragment>;
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>;
} & CoaFormSectionProps) => {
  const { control, register, watch } = form;

  const {
    fields: vesselFields,
    append: appendVessel,
    remove: removeVessel,
  } = useFieldArray({
    control,
    name: 'vessels',
    keyName: 'key',
  });

  const watchedShipownerOrgId = watch('shipownerOrgId');

  const { fields: assortmentFields } = assortmentFieldMethods;

  const formAssortments = watch('assortments', assortmentFields) ?? [];
  const assortments = formAssortments
    .map(mapIndex)
    .filter(
      (
        field,
      ): field is typeof field &
        Pick<Required<typeof field>, 'assortmentId' | 'id'> =>
        field.assortmentId !== undefined && field.id !== undefined,
    )
    .map(mapAssortmentName(assortmentById, assortmentGroupById))
    .sort(sortByAssortmentName);

  const vesselOptions = useMemo(() => {
    const shipownerId = watchedShipownerOrgId.value;

    if (shipownerId) {
      const shipowners = data?.shipowners?.nodes ?? [];
      const shipowner = shipowners
        .filter(isDefined)
        .find((shipowner) => shipowner.id === shipownerId);

      return nodesToOptions(shipowner?.shipsByOrganizationId?.nodes ?? []);
    } else {
      return [];
    }
  }, [watchedShipownerOrgId, data]);

  return (
    <>
      <FormSection title="Freight rates">
        <Box display="flex" flexDirection="column">
          <Text fontWeight="bold" mb={4}>
            Freight rates per port pair
          </Text>
          <Box
            display="flex"
            flexDirection="column"
            borderColor="neutral.gray_225"
            borderWidth={1}
            borderStyle="solid"
            overflowX="auto"
            overflowY="auto"
            height="500px"
          >
            <Table style={fixedTableLayout}>
              <thead>
                <TableHeaders isSticky>
                  <TableHeader width={200}>Port pair</TableHeader>
                  {assortments.map((assortment) => (
                    <TableHeader width={200} key={assortment.id}>
                      {assortment.assortmentName ??
                        assortment.assortmentGroupName ??
                        'Undefined commodity'}
                    </TableHeader>
                  ))}
                  <TableHeader />
                </TableHeaders>
              </thead>
              <tbody>
                <TableRow isMuted>
                  <TableCell>Amount</TableCell>
                  {assortments.map((assortment) => (
                    <TableCell key={assortment.id}>
                      <Text>
                        {assortment.minAmount} - {assortment.maxAmount}
                      </Text>
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
                <TableRow isMuted>
                  <TableCell>Payment unit</TableCell>
                  {assortments.map((assortment) => (
                    <TableCell key={assortment.id}>
                      <Select
                        {...register(
                          `assortments.${assortment.index}.paymentUnit`,
                        )}
                        options={paymentUnitsToOptions(paymentUnits)}
                        defaultValue={assortment.paymentUnit}
                      />
                    </TableCell>
                  ))}
                  <TableCell />
                </TableRow>
                {portPairs.map((portPair, portPairIndex) => (
                  <TableRow key={portPair.id} isMuted={portPairIndex % 2 !== 0}>
                    <TableCell>
                      <Text>{`${
                        portPair.originId?.label ?? portPair.originZoneId?.label
                      } - ${
                        portPair.destinationId?.label ??
                        portPair.destinationZoneId?.label
                      }`}</Text>
                    </TableCell>
                    {assortments.map((coaAssortment, assortmentIndex) => (
                      <TableCell key={coaAssortment.id}>
                        <input
                          type="hidden"
                          {...register(
                            getPriceField(portPair.id, coaAssortment.id, 'id'),
                            { setValueAs: stringToNumber },
                          )}
                        />
                        <input
                          type="hidden"
                          {...register(
                            getPriceField(
                              portPair.id,
                              coaAssortment.id,
                              'shipId',
                            ),
                            { setValueAs: stringToNumber },
                          )}
                          readOnly={isReadonly}
                        />
                        <Input
                          type="number"
                          {...register(
                            getPriceField(
                              portPair.id,
                              coaAssortment.id,
                              'price',
                            ),
                            { setValueAs: stringToNumber },
                          )}
                          readOnly={isReadonly}
                          data-testid={`price-${portPairIndex}-${assortmentIndex}`}
                        />
                      </TableCell>
                    ))}
                    <TableCell />
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </Box>
        </Box>
      </FormSection>
      <FormSection title="Vessels" muted>
        <Box display="flex" flexDirection="column" alignItems="flex-start">
          {vesselFields.map((vessel, index) => (
            <FormRow key={vessel.key}>
              <Input
                type="hidden"
                {...register(`vessels.${index}.id`)}
                defaultValue={vessel.id ?? ''}
              />
              <FormField
                label={index === 0 ? 'Vessel' : undefined}
                width={200}
                mb={0}
                data-testid={`vessel-${index}`}
              >
                <Controller
                  name={`vessels.${index}.shipId`}
                  control={control}
                  defaultValue={vessel.shipId}
                  render={({ field }) => (
                    <ReactSelect
                      styles={reactSelectStyles}
                      options={vesselOptions}
                      menuPlacement="auto"
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField
                label={index === 0 ? 'Demurrage' : undefined}
                width={200}
                mb={0}
              >
                <Input
                  type="number"
                  {...register(`vessels.${index}.demurrage`, {
                    setValueAs: stringToNumber,
                  })}
                  defaultValue={vessel.demurrage || undefined}
                />
              </FormField>
              {!isReadonly && (
                <Box display="flex" alignSelf="flex-end" pb={3}>
                  <DeleteButton onPress={() => removeVessel(index)} />
                </Box>
              )}
            </FormRow>
          ))}
          {!isReadonly && (
            <Button
              variant="secondary"
              onPress={() =>
                appendVessel({
                  id: null,
                  shipId: { value: NaN, label: '' },
                  demurrage: null,
                })
              }
              mt={3}
              data-testid="add-vessel"
            >
              + Add vessel
            </Button>
          )}
        </Box>
      </FormSection>
    </>
  );
};

const commissionTypeOptions = [
  {
    value: CommissionType.LUMPSUM,
    label: 'Amount',
  },
  {
    value: CommissionType.PERCENTAGE,
    label: 'Percentage',
  },
  {
    value: CommissionType.RATE,
    label: 'Rate',
  },
];

function CommissionFields({
  append,
  fields,
  register,
  remove,
  control,
  watch,
  refetch,
  setValue,
  organizationOptions,
  portPairs,
  assortmentFieldMethods,
  assortmentById,
  assortmentGroupById,
}: {
  append: UseFieldArrayAppend<CoaFormFields, 'commissions'>;
  fields: Array<FieldArrayWithId<CoaFormFields, 'commissions', 'key'>>;
  register: UseFormRegister<CoaFormFields>;
  remove: UseFieldArrayRemove;
  control: Control<CoaFormFields>;
  watch: UseFormWatch<CoaFormFields>;
  refetch: (
    variables?:
      | Partial<
          Exact<{
            unsavedOrderIds?: Maybe<number[]> | undefined;
            outgoing: boolean;
          }>
        >
      | undefined,
  ) => Promise<ApolloQueryResult<CoaFormQuery>>;
  setValue: UseFormSetValue<CoaFormFields>;
  organizationOptions: {
    value: number;
    label: string;
  }[];
  assortmentFieldMethods: UseFieldArrayReturn<
    CoaFormFields,
    'assortments',
    'key'
  >;
  assortmentById: Map<number, CoaForm_AssortmentFragment>;
  assortmentGroupById: Map<number, CoaForm_AssortmentGroupFragment>;
  portPairs: PortPairType[];
}): JSX.Element {
  const [createOrganization, { loading: createOrganizationLoading }] =
    useCreateOrganizationMutation();
  const currentUser = useCurrentUser();
  const organisationId = currentUser?.organizationId ?? null;

  const { fields: assortmentFields } = assortmentFieldMethods;

  const watchedAssortments = assortmentFields.length
    ? (watch('assortments', assortmentFields as AssortmentType[]) ?? [])
    : [];

  const assortmentOptions = [
    { value: '', label: 'Apply to all commodities' },
    ...watchedAssortments
      .map(mapAssortmentName(assortmentById, assortmentGroupById))
      .sort(sortByAssortmentName)
      .map((field) => ({
        value: field.id,
        label: `${
          field.assortmentName ??
          field.assortmentGroupName ??
          'Commodity name N/A'
        } ${field.minAmount ?? ''} - ${field.maxAmount ?? ''}`,
      })),
  ];

  const routeOptions = [
    { value: '', label: 'Apply to all routes' },
    ...portPairs.map((portPair) => ({
      value: portPair.id,
      label: `${portPair.originId?.label} - ${portPair.destinationId?.label}`,
    })),
  ];

  const handleCreateOrganization = useCallback(
    async (inputValue: string, idx: number) => {
      const { data } = await createOrganization({
        variables: {
          input: {
            organization: {
              name: inputValue,
              parentOrganizationId: organisationId,
            },
          },
        },
      });

      const { id, name } = { ...data?.createOrganization?.organization };
      setValue(`commissions.${idx}.organization`, {
        value: id ?? 0,
        label: name ?? '',
      });
      await refetch();
    },
    [createOrganization, refetch, organisationId, setValue],
  );

  return (
    <FormSection title="Commissions" display="block">
      <Box
        borderWidth={1}
        borderStyle="solid"
        borderColor="neutral.gray_225"
        overflow="visible"
        mb={3}
      >
        <Table>
          <thead>
            <TableHeaders>
              <TableHeader width={200}>Type</TableHeader>
              <TableHeader width={200}>Value</TableHeader>
              <TableHeader width={200}>Organization</TableHeader>
              <TableHeader width={200}>Commodity</TableHeader>
              <TableHeader width={200}>Route</TableHeader>
              <TableHeader width={200}>Apply on BAF</TableHeader>
              <TableHeader width={200}>Apply on extra freight</TableHeader>
              <TableHeader width={200}>Apply on demurrage</TableHeader>
              <TableHeader>Actions</TableHeader>
            </TableHeaders>
          </thead>
          <tbody>
            {fields.map((f, i) => {
              return (
                <TableRow key={f.key}>
                  <TableCell>
                    <Select
                      {...register(`commissions.${i}.type`, {
                        setValueAs: valueOrNull,
                      })}
                      options={commissionTypeOptions}
                      placeholder="-"
                      enablePlaceholder
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      {...register(`commissions.${i}.value`, {
                        setValueAs: stringToNumber,
                      })}
                    />
                  </TableCell>
                  <TableCell>
                    <Controller
                      name={`commissions.${i}.organization`}
                      control={control}
                      render={({ field }) => {
                        return (
                          <CreatableSelect
                            isLoading={createOrganizationLoading}
                            isDisabled={createOrganizationLoading}
                            styles={reactSelectStyles}
                            options={organizationOptions}
                            onCreateOption={(inputValue) =>
                              handleCreateOrganization(inputValue, i)
                            }
                            {...field}
                          />
                        );
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      {...register(`commissions.${i}.coaAssortmentId`)}
                      defaultValue={f.coaAssortmentId || undefined}
                      options={assortmentOptions}
                      data-testid={`commission-${i}-assortment`}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      {...register(`commissions.${i}.coaRouteId`)}
                      defaultValue={f.coaRouteId || undefined}
                      options={routeOptions}
                      data-testid={`commission-${i}-route`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.bafApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.extraFreightApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="checkbox"
                      {...register(`commissions.${i}.demurrageApplied`)}
                      style={CHECK_BOX_SIZE}
                    />
                  </TableCell>
                  <TableCell>
                    <Button variant="secondary" onPress={() => remove(i)}>
                      Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </tbody>
        </Table>
      </Box>
      <Button
        variant="secondary"
        onPress={() =>
          append({
            type: CommissionType.LUMPSUM,
            value: 0,
            id: null,
            organization: {
              label: null,
              value: 0,
            },
            demurrageApplied: null,
            extraFreightApplied: null,
            bafApplied: null,
            coaCommissionId: null,
            coaRouteId: null,
            coaAssortmentId: null,
          })
        }
      >
        Add commission item
      </Button>
    </FormSection>
  );
}

const PortCosts = ({
  append,
  fields,
  form,
  remove,
  isReadonly,
  portPairs,
  vesselAttributes,
}: {
  append: UseFieldArrayAppend<CoaFormFields, 'portCosts'>;
  fields: Partial<FieldArrayWithId<CoaFormFields, 'portCosts', 'key'>>[];
  remove: (index?: number | number[] | undefined) => void;
  portPairs: PortPairType[];
  vesselAttributes: {
    value: number;
    label: string;
  }[];
} & CoaFormSectionProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const watchVessels = watch('vessels');

  const vesselOptions =
    watchVessels?.map((entry) => {
      return entry.shipId;
    }) ?? [];

  const portOptions = useMemo(() => {
    const visistedPortId = new Set<number>();
    const returnedValues: {
      value: number;
      label: string;
    }[] = [];
    if (!portPairs) {
      return returnedValues;
    }
    for (const portPair of portPairs) {
      const originPort = portPair.originId;
      const destinationPort = portPair.destinationId;
      if (originPort && !visistedPortId.has(originPort.value)) {
        visistedPortId.add(originPort.value);
        returnedValues.push({ ...originPort });
      }
      if (destinationPort && !visistedPortId.has(destinationPort.value)) {
        visistedPortId.add(destinationPort.value);
        returnedValues.push({ ...destinationPort });
      }
    }
    return returnedValues;
  }, [portPairs]);

  return (
    <FormSection title="Port costs" muted>
      <Box display="flex" flexDirection="column" alignItems="flex-start">
        {fields.map((entry, index) => {
          return (
            <FormRow key={entry.key}>
              <Input
                type="hidden"
                {...register(`portCosts.${index}.id`)}
                defaultValue={entry.id ?? ''}
              />
              <FormField
                label={index === 0 ? 'Vessel name' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portCosts?.[index]?.shipId}
              >
                <Controller
                  name={`portCosts.${index}.shipId`}
                  control={control}
                  defaultValue={entry.shipId}
                  render={({ field: { onChange, value, ...rest } }) => {
                    return (
                      <ReactSelect
                        styles={reactSelectStyles}
                        options={vesselOptions}
                        menuPlacement="auto"
                        isSearchable={true}
                        onChange={(selectedOption) => {
                          onChange(selectedOption);
                          setValue(`portCosts.${index}.vesselTypeId`, {
                            label: '',
                            value: NaN,
                          });
                        }}
                        value={value}
                        {...rest}
                      />
                    );
                  }}
                />
              </FormField>
              <FormField
                label={index === 0 ? 'Vessel class' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portCosts?.[index]?.vesselTypeId}
              >
                <Controller
                  name={`portCosts.${index}.vesselTypeId`}
                  control={control}
                  defaultValue={entry.vesselTypeId}
                  render={({ field: { onChange, ...rest } }) => (
                    <ReactSelect
                      styles={reactSelectStyles}
                      options={vesselAttributes}
                      menuPlacement="auto"
                      isSearchable={true}
                      onChange={(selectedOption) => {
                        onChange(selectedOption);
                        setValue(`portCosts.${index}.shipId`, {
                          label: '',
                          value: NaN,
                        });
                      }}
                      {...rest}
                    />
                  )}
                />
              </FormField>
              <FormField
                label={index === 0 ? 'Port' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portCosts?.[index]?.locationId}
                data-testid={`portCost-port-${index}`}
              >
                <Controller
                  name={`portCosts.${index}.locationId`}
                  control={control}
                  defaultValue={entry.locationId}
                  render={({ field }) => (
                    <ReactSelect
                      styles={reactSelectStyles}
                      options={portOptions}
                      menuPlacement="auto"
                      {...field}
                    />
                  )}
                />
              </FormField>
              <FormField
                label={index === 0 ? 'Cost' : undefined}
                width={200}
                mb={0}
                hasError={!!errors.portCosts?.[index]?.cost}
              >
                <Input
                  type="number"
                  {...register(`portCosts.${index}.cost`, {
                    setValueAs: stringToNumber,
                  })}
                  defaultValue={entry.cost || 0}
                  required
                />
              </FormField>
              {!isReadonly && (
                <Box display="flex" alignSelf="flex-end" pb={3}>
                  <DeleteButton onPress={() => remove(index)} />
                </Box>
              )}
            </FormRow>
          );
        })}
        {!isReadonly && (
          <Button
            variant="secondary"
            onPress={() =>
              append({
                id: null,
                cost: 0,
                locationId: {
                  value: NaN,
                  label: '',
                },
                shipId: {
                  value: NaN,
                  label: '',
                },
                vesselTypeId: {
                  value: NaN,
                  label: '',
                },
              })
            }
            mt={3}
            data-testid="add-port-cost"
          >
            + Add port cost
          </Button>
        )}
      </Box>
    </FormSection>
  );
};

export default CoaForm;
